/**
 * Noms des participants d'une salle VUS PAR L'UTILISATEUR CONNECTÉ (RPC
 * `get_visio_room_participants`, qui passe par `name_for_viewer`, migrations
 * 0661-0662) : le prof voit le vrai nom de ses élèves, les autres élèves leur
 * pseudo. Chaque client n'annonce en direct que son nom public (pseudo) ; ces
 * noms le remplacent sur les vignettes et dans le panneau « Participants ».
 * Échec de lecture : on garde les noms annoncés (jamais d'écran cassé).
 */

import { useEffect, useMemo, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';

/** Remplace le nom annoncé par celui du serveur quand il existe. PUR. */
export function withServerNames<T extends { userId: string; displayName: string }>(
  participants: readonly T[],
  names: ReadonlyMap<string, string>,
): T[] {
  return participants.map((p) => {
    const name = names.get(p.userId);
    return name ? { ...p, displayName: name } : p;
  });
}

export function useParticipantNames(
  db: SupabaseClient,
  roomId: string,
  participantIds: readonly string[],
): Map<string, string> {
  const [names, setNames] = useState<Map<string, string>>(() => new Map());
  // Relecture quand quelqu'un arrive (un nouveau participant n'est pas encore connu).
  const key = [...participantIds].sort().join(',');
  useEffect(() => {
    if (!roomId || typeof (db as { rpc?: unknown }).rpc !== 'function') return;
    let cancelled = false;
    Promise.resolve(db.rpc('get_visio_room_participants', { p_room_id: roomId }))
      .then(({ data, error }) => {
        if (cancelled || error) return;
        const rows = (data ?? []) as Array<{ user_id: string; display_name: string | null }>;
        setNames(
          new Map(rows.filter((r) => r.display_name).map((r) => [r.user_id, r.display_name!])),
        );
      })
      .catch(() => {
        /* noms annoncés gardés */
      });
    return () => {
      cancelled = true;
    };
  }, [db, roomId, key]);
  return useMemo(() => names, [names]);
}
